import React, { useMemo } from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import { CmsImageSchema, CmsLinkSchema, CmsShortTextSchema } from '$cms/types';
import type { ContentComponent } from '$cms/ContentComponent';
import { useModalQueue } from '$util/hooks/useModalQueue';
import type { SchemaType } from '$util/schema';
import { CustomerCarouselItem } from '$quantum/molecules/CustomerCarouselItem';
import { Headings } from '$quantum/molecules/Headings';
import { CarouselBase } from '$quantum/molecules/CarouselBase';
import { Grid, Row, Col } from '@/components/atoms/Grid';

import styles from './index.module.scss';

const defaultConfiguration = {
  backgroundColor: 'transparent',
};

export const CustomerCarouselSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema,
    subHeading: CmsShortTextSchema.optional(),
    slideName: z.record(CmsShortTextSchema),
    slideQuote: z.record(CmsShortTextSchema),
    slideSaved: z.record(CmsShortTextSchema),
    slideService: z.record(CmsShortTextSchema),
    slideImage: z.record(CmsImageSchema),
    slideVideo: z.record(CmsLinkSchema).optional(),
  }),
  configuration: z
    .object({
      backgroundColor: z.string().optional(),
    })
    .default(defaultConfiguration),
};

export type CustomerCarouselTranslatedEntities = SchemaType<
  (typeof CustomerCarouselSchema)['translatedEntities']
>;
export type CustomerCarouselConfiguration = SchemaType<(typeof CustomerCarouselSchema)['configuration']>;

/**
 * Component that renders Text, Images or Video in Carousel.<br />
 * https://www.notion.so/fixter/CustomerCarousel-8510dbc9e5e34b548588e9e6c52a0f83
 */
export const CustomerCarousel: ContentComponent<
  CustomerCarouselTranslatedEntities,
  CustomerCarouselConfiguration
> = function CustomerCarousel({ translatedEntities, configuration = defaultConfiguration, className }) {
  const { heading, slideName, slideQuote, slideSaved, slideService, slideImage, slideVideo, subHeading } =
    translatedEntities;
  const { backgroundColor } = configuration;
  const [, queueModal] = useModalQueue();

  const carouselItems = useMemo<JSX.Element[]>(
    () =>
      Object.entries(slideName).map(([index, itemName]) => {
        const name = itemName.value;
        const quote = slideQuote[index].value;
        const saved = slideSaved[index].value;
        const service = slideService[index].value;
        const image = slideImage[index];
        const slideVideoUrl = slideVideo?.[index]?.url;
        return (
          <CustomerCarouselItem
            key={index}
            name={name}
            quote={quote}
            saved={saved}
            service={service}
            image={image}
            videoUrl={slideVideoUrl}
            onVideoButtonClick={() => {
              if (slideVideoUrl) {
                queueModal([
                  // eslint-disable-next-line react/jsx-key
                  <div className={styles.iframeContainer}>
                    <iframe
                      title="Fixter Video"
                      className={styles.videoIFrame}
                      src={`${slideVideoUrl}?autoplay=1`}
                      width="100%"
                      allow="autoplay; fullscreen"
                      allowFullScreen
                    />
                  </div>,
                  'modal',
                ]);
              }
            }}
          />
        );
      }),
    [slideName, slideQuote, slideSaved, slideService, slideImage, slideVideo, queueModal]
  );

  return (
    <Grid
      className={classNames(CustomerCarousel.displayName, styles.component, className)}
      style={{
        // @ts-ignore Pass prop to styles
        '--bg-color': backgroundColor,
      }}
    >
      <Row className={styles.bleed}>
        <Col>
          <Headings className={styles.headings} heading={heading.value} subheading={subHeading?.value} />
          <CarouselBase>{carouselItems}</CarouselBase>
        </Col>
      </Row>
    </Grid>
  );
};

CustomerCarousel.displayName = 'CustomerCarousel';
CustomerCarousel.schema = CustomerCarouselSchema;
